import React, { useState } from "react";
import {
  Box,
  Drawer,
  Fab,
  IconButton,
  Tooltip,
  Typography,
  Theme,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import NewReservationForm from "../../../features/newReservation";
import { Reservation } from "../../reservations/types/Reservation";
import { useCloseOnBack } from "../../shared/hooks/useCloseOnBack";

interface FloatingActionButtonProps {
  tooltip?: string;
  drawerWidth?: number;
  onSuccess?: () => void;
  onClose?: () => void;
  existingReservations?: Reservation[];
  initialData?: Partial<Reservation>;
  soloEstacionamiento?: boolean;
}

const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  tooltip = "Nueva reserva",
  drawerWidth = 400,
  onSuccess,
  onClose,
  existingReservations = [],
  initialData,
  soloEstacionamiento = false,
}: FloatingActionButtonProps) => {
  const [open, setOpen] = useState(false);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const resolvedDrawerWidth = isMobile ? "100vw" : drawerWidth;

  // Abrir automáticamente si hay datos iniciales
  React.useEffect(() => {
    if (initialData) {
      setOpen(true);
    }
  }, [initialData]);

  const handleClose = () => {
    setOpen(false);
    onClose?.(); // Llamar callback para limpiar estado en el componente padre
  };

  useCloseOnBack(open, handleClose);

  return (
    <>
      <Tooltip title={tooltip} placement="left">
        <Fab
          color="primary"
          variant={isMobile ? "circular" : "extended"}
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            bottom: { xs: 12, sm: 16 },
            right: { xs: 12, sm: 16 },
            zIndex: (theme: Theme) => theme.zIndex.drawer - 1,
            minHeight: { xs: 48, sm: 48 },
            px: { xs: 0, sm: 2 },
          }}
        >
          <AddIcon />
          {!isMobile && (
            <Typography component="span" sx={{ ml: 1, fontWeight: 700 }}>
              Nueva reserva
            </Typography>
          )}
        </Fab>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        transitionDuration={300}
        sx={{
          width: resolvedDrawerWidth,
          flexShrink: 0,
          zIndex: (theme: Theme) => theme.zIndex.modal,
          "& .MuiDrawer-paper": {
            width: resolvedDrawerWidth,
            height: { xs: "100dvh", sm: "100%" },
            boxSizing: "border-box",
            borderTopLeftRadius: { xs: 0, sm: 16 },
            borderBottomLeftRadius: { xs: 0, sm: 16 },
            padding: 0,
            overflow: "hidden",
            transition:
              "transform 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 0ms !important",
          },
        }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
            "& > *": {
              mb: 0,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: { xs: 1, sm: 0.75 },
              minHeight: { xs: 40, sm: "auto" },
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              Nueva Reserva
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <NewReservationForm
              key={open ? "open" : "closed"}
              onClose={handleClose}
              onSuccess={() => {
                onSuccess?.();
                handleClose();
              }}
              existingReservations={existingReservations}
              initialData={initialData as Reservation}
              soloEstacionamiento={soloEstacionamiento}
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default FloatingActionButton;
